import { readOnly, equal, not } from '@ember/object/computed';
import { computed } from '@ember/object';
import { attr } from '@ember-data/model';
import { fragment } from 'ember-data-model-fragments/attributes';
import { inject as service } from '@ember/service';
import BaseFragment from '@square/dashboard-data/models/ember-data-base-fragment';
import Enum from '@square/dashboard-utilities/utils/enum';
import Money from '@square/dashboard-money/models/money';
import InventoryQuantity from '@square/dashboard-items-data/models/squareup/inventory/v3/inventory-quantity';
export const PurchaseOrderLineState = new Enum(['PO_LINE_DO_NOT_USE', 'PO_LINE_OPEN', 'PO_LINE_RECEIVED', 'PO_LINE_DAMAGED', 'PO_LINE_CANCELED', 'PO_LINE_ARCHIVED']);
export const PURCHASE_ORDER_LINE_NOTES_MAX_LENGTH = 4096;
const PurchaseOrderLine = BaseFragment.extend({
  store: service(),
  lineNumber: attr('number'),
  parentLineNumber: attr('number'),
  product: fragment('squareup/inventory/v3/purchase-order-line-product'),
  quantity: attr('proto-base-model', {
    modelClass: InventoryQuantity
  }),
  unitCostAmount: attr('money-proto'),
  totalCostAmount: attr('money-proto'),
  feeAmount: attr('money-proto'),
  taxAmount: attr('money-proto'),
  state: attr('enum', {
    enumClass: PurchaseOrderLineState
  }),
  stateChangedAt: attr('number'),
  stateChangedBy: attr('string'),
  notes: attr('string'),
  createdAt: attr('number'),
  updatedAt: attr('number'),
  undoAction() {},
  updateTotalCostAmount() {
    this.set('totalCostAmount', (this.unitCostAmount || Money.zero()).multiply(this.quantity?.count));
  },
  isDirty: computed('_dirtyAttributes.[]', 'product.isDirty', function () {
    return this.product?.isDirty || this._super();
  }),
  unitCostWithFeeAmount: computed('unitCostAmount', 'feeAmount', function () {
    try {
      const unitCost = this.unitCostAmount || Money.zero();
      const fee = this.feeAmount || Money.zero(unitCost.get('currencyCode'));
      return unitCost.add(fee);
    } catch {
      return Money.zero();
    }
  }),
  quantityFormatted: readOnly('quantity.countFormatted'),
  amount: computed('quantityFormatted', 'totalCostAmount.amount', 'unitCostAmount.amount', function () {
    if (this.totalCostAmount?.amount) {
      return this.totalCostAmount;
    }
    const quantity = this.quantityFormatted || 0;
    const cost = this.unitCostAmount || Money.zero();
    return cost.multiply(quantity);
  }),
  amountWithFee: computed('feeAmount', 'quantity.count', 'totalCostAmount.amount', 'unitCostWithFeeAmount.amount', function () {
    try {
      const quantity = this.quantity?.count || 0;
      if (this.totalCostAmount?.amount) {
        const totalCostAmount = this.totalCostAmount || Money.zero();
        const feeAmount = this.feeAmount || Money.zero();
        return feeAmount.multiply(quantity).add(totalCostAmount);
      }
      const cost = this.unitCostWithFeeAmount || Money.zero();
      return cost.multiply(quantity);
    } catch {
      return Money.zero();
    }
  }),
  productName: readOnly('product.productName'),
  isOpen: equal('state', 'PO_LINE_OPEN'),
  isReceived: equal('state', 'PO_LINE_RECEIVED'),
  isNotArchived: computed('state', function () {
    return this.state !== 'PO_LINE_ARCHIVED';
  }),
  isAddingToTotal: computed('state', function () {
    return this.state === 'PO_LINE_OPEN' || this.state === 'PO_LINE_RECEIVED';
  }),
  isNotAddingToTotal: not('isAddingToTotal')
}).reopenClass({
  _modelNameForMigration: 'squareup/inventory/v3/purchase-order-line'
});
export default PurchaseOrderLine;